export interface Size {
  width: number;
  height: number;
}

export interface ZoomLevels {
  doubleTap: number;
  max: number;
}

const DESKTOP_MARGIN = 56;
const MIN_SIZE = 400;
const LOWER_SCALE_FRICTION = 0.3;
const UPPER_SCALE_FRICTION = 0.1;
const DECELERATION_RATE = 0.995;

export const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export function getFitSize(
  item: Size,
  viewWidth: number,
  viewHeight: number,
  mobile: boolean,
): Size {
  const margin = mobile ? 0 : DESKTOP_MARGIN;
  if (!item.width || !item.height) {
    return { width: viewWidth - margin * 2, height: viewHeight - margin * 2 };
  }
  const rate = item.width / item.height;
  if (rate > viewWidth / viewHeight) {
    const width = Math.min(viewWidth - margin * 2, Math.max(item.width, MIN_SIZE));
    return { width, height: width / rate };
  }
  const height = Math.min(viewHeight - margin * 2, Math.max(item.height, MIN_SIZE));
  return { width: height * rate, height };
}

// scale is relative to the fitted size; 1 = fit, natural = 1:1 pixels
export function getZoomLevels(item: Size | undefined, fit: Size): ZoomLevels {
  const natural = item && item.width > 0 && fit.width > 0 ? item.width / fit.width : 1;
  return {
    doubleTap: clamp(natural, 2, 3),
    max: clamp(natural, 4, 10),
  };
}

export function getPanBounds(
  fit: Size,
  scale: number,
  viewWidth: number,
  viewHeight: number,
) {
  return {
    x: Math.max(0, (fit.width * scale - viewWidth) / 2),
    y: Math.max(0, (fit.height * scale - viewHeight) / 2),
  };
}

export function zoomAtPoint(
  fx: number,
  fy: number,
  x0: number,
  y0: number,
  s0: number,
  s1: number,
) {
  const k = s1 / s0;
  return { x: fx - (fx - x0) * k, y: fy - (fy - y0) * k };
}

export function frictionScale(scale: number, min: number, max: number) {
  if (scale < min) {
    return min - (min - scale) * LOWER_SCALE_FRICTION;
  }
  if (scale > max) {
    return max + (scale - max) * UPPER_SCALE_FRICTION;
  }
  return scale;
}

// same curve as @use-gesture rubberbandIfOutOfBounds
export function rubberband(value: number, min: number, max: number, constant = 0.15) {
  const dimension = max - min;
  const band = (distance: number) =>
    dimension === 0
      ? Math.pow(distance, constant * 5)
      : (distance * dimension * constant) / (dimension + constant * distance);
  if (value < min) {
    return min - band(min - value);
  }
  if (value > max) {
    return max + band(value - max);
  }
  return value;
}

export function project(velocity: number) {
  return (velocity * DECELERATION_RATE) / (1 - DECELERATION_RATE);
}
